import { Pill } from '@mes-amis/galette-design-system';
import { describeServiceUnits, interactionTypeLabel, type CaseNote, type NoteOptions } from '@/db/types/case_note';
import NoteStatusPill from './NoteStatusPill';
import { formatNoteDate } from './format';

interface NoteCardProps {
  note: CaseNote;
  options?: NoteOptions | null;
  currentPersonId?: number | null;
  onOpen: () => void;
}

/** One note in a client's Notes tab. */
export default function NoteCard({ note, options, currentPersonId, onOpen }: NoteCardProps) {
  const interaction = interactionTypeLabel(note, options);
  const subject = note.note_subject?.name ?? note.subject;
  const context = [note.type === 'CaseNote' ? 'Case note' : null, note.program?.name, subject].filter(Boolean).join(' · ');
  const units = describeServiceUnits(note.service_units);
  const author = note.author && currentPersonId != null && note.author.id === currentPersonId ? 'You' : note.author?.name;
  const refused = !note.synced && Boolean(note.sync_error_message);

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="note-card"
      className={`w-full text-left bg-white border rounded-lg px-4 py-3 space-y-1.5 hover:border-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-deep-sea-green ${
        refused ? 'border-red-200' : 'border-gray-200'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-gray-900 truncate">
          {formatNoteDate(note.interaction_date)}
          {interaction && <span className="font-normal text-gray-600"> · {interaction}</span>}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {note.type === 'BasicNote' && <Pill text="Basic note" color="lavender" size="sm" />}
          <NoteStatusPill note={note} />
        </span>
      </div>
      {context && <p className="text-xs text-gray-500 truncate">{context}</p>}
      <p className="text-sm text-gray-700 line-clamp-2 whitespace-pre-line">{note.detail}</p>
      {refused && <p className="text-xs font-medium text-red-700">{note.sync_error_message}</p>}
      {(units.length > 0 || author) && (
        <div className="flex justify-between gap-3 text-xs text-gray-500">
          <span className="truncate">{units.join(' · ')}</span>
          {author && <span className="shrink-0">{author}</span>}
        </div>
      )}
    </button>
  );
}
